import React, { useState, useEffect, useMemo } from 'react';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  fetchActiveOrganizationClientsWithBalances,
  getClientBalanceValue,
} from '../utils/clientBalances';
import {
  Users, UserPlus, Search, Edit2,
  Trash2, CreditCard, ShoppingCart, Zap, DollarSign,
  ArrowLeft, ArrowRight, User, Smartphone,
  RefreshCcw, Info, MapPin, Mail, ChevronUp, ChevronDown,
  Power, ShieldAlert
} from 'lucide-react';

const ADMIN_ROLES = new Set(['admin', 'administrador']);
const CLIENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
  { value: 'all', label: 'Todos' },
];

// Nota: Evitamos popouts/toasts. Usamos banners inline en el HTML.

/**
 * Gestión de Cartera de Clientes - Estándar Impulza Enterprise
 * Versión simplificada sin menú de acciones
 * ADAPTADO AL ESTILO "PRODUCTOS"
 */
const Clientes = ({ onNavigate }) => {
  const { profile, organization } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [uiMessage, setUiMessage] = useState(null); // { type: 'success'|'error'|'info', text: string }
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });
  const [statusModal, setStatusModal] = useState(null); // { mode, client, reason }
  const [savingStatus, setSavingStatus] = useState(false);

  const isAdmin = ADMIN_ROLES.has(String(profile?.role || '').toLowerCase());
  const allowClientDeactivation = organization?.allow_client_deactivation === true;

  useEffect(() => {
    if (profile?.organization_id) fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, statusFilter]);

  useEffect(() => {
    if (!allowClientDeactivation && statusFilter !== 'active') {
      setStatusFilter('active');
    }
  }, [allowClientDeactivation, statusFilter]);

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const { data, error } = await fetchActiveOrganizationClientsWithBalances({
        organizationId: profile.organization_id,
        supabaseClient: supabase,
        statusFilter,
      });
      if (error) throw error;
      setClientes(data || []);
      setUiMessage(null);
    } catch (error) {
      reportHandledError(error, { feature: 'clientes:listado' });
      setUiMessage({ type: 'error', text: 'Error al sincronizar base de datos.' });
    } finally {
      setLoading(false);
    }
  };

  const requestStatusChange = (cliente, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!isAdmin) {
      setUiMessage({ type: 'error', text: 'Solo un administrador puede activar o desactivar clientes.' });
      return;
    }
    const isCurrentlyActive = cliente.is_active !== false;
    if (isCurrentlyActive && !allowClientDeactivation) {
      setUiMessage({ type: 'error', text: 'La baja de clientes está deshabilitada en Mi Empresa.' });
      return;
    }
    setStatusModal({
      mode: isCurrentlyActive ? 'deactivate' : 'activate',
      client: cliente,
      reason: '',
    });
  };

  const confirmStatusChange = async () => {
    if (!statusModal?.client?.id || !profile?.organization_id) return;
    const isDeactivating = statusModal.mode === 'deactivate';
    const trimmedReason = String(statusModal.reason || '').trim();

    setSavingStatus(true);
    try {
      const payload = isDeactivating
        ? {
            is_active: false,
            deactivated_at: new Date().toISOString(),
            deactivation_reason: trimmedReason || null,
          }
        : {
            is_active: true,
            deactivated_at: null,
            deactivation_reason: null,
          };

      const { error } = await supabase
        .from('clients')
        .update(payload)
        .eq('organization_id', profile.organization_id)
        .eq('id', statusModal.client.id);
      if (error) throw error;

      setStatusModal(null);
      setUiMessage({
        type: 'success',
        text: isDeactivating ? 'Cliente desactivado.' : 'Cliente reactivado.',
      });
      await fetchClientes();
    } catch (err) {
      console.error(err);
      setUiMessage({ type: 'error', text: 'No se pudo cambiar el estado del cliente.' });
    } finally {
      setSavingStatus(false);
    }
  };

  const filteredClientes = useMemo(() => {
    return clientes.filter(c => 
      c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || c.doc_number?.includes(searchTerm)
    );
  }, [clientes, searchTerm]);

  const stats = useMemo(() => {
    const deuda = clientes.reduce((acc, c) => acc + getClientBalanceValue(c), 0);
    const credito = clientes.reduce((acc, c) => acc + (c.credit_limit || 0), 0);
    return { deuda, credito };
  }, [clientes]);

  const sortedClientes = useMemo(() => {
    const items = [...filteredClientes];
    const { key, direction } = sortConfig;

    return items.sort((a, b) => {
      const valueMap = {
        ref: [a.full_name || '', b.full_name || ''],
        full_name: [a.full_name || '', b.full_name || ''],
        identificacion: [`${a.doc_type || ''} ${a.doc_number || ''}`, `${b.doc_type || ''} ${b.doc_number || ''}`],
        balance: [getClientBalanceValue(a), getClientBalanceValue(b)],
      };

      const [valueA, valueB] = valueMap[key] || [a.full_name || '', b.full_name || ''];
      if (typeof valueA === 'number' || typeof valueB === 'number') {
        return direction === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }
      const safeA = String(valueA).toLowerCase();
      const safeB = String(valueB).toLowerCase();
      if (safeA < safeB) return direction === 'asc' ? -1 : 1;
      if (safeA > safeB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredClientes, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const FilterHint = () => <div className="h-2.5 w-2.5 opacity-30" />;

  const SortableTh = ({ label, sortKey, align = 'left' }) => {
    const isActive = sortConfig.key === sortKey;

    return (
      <th
        className={`px-3.5 py-3.5 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span>{label}</span>
          <span className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <FilterHint />}
          </span>
        </div>
      </th>
    );
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
      <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Sincronizando Cartera...</p>
    </div>
  );

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER NUEVO ESTILO PRODUCTOS */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Users size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Cartera de Clientes</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Administración de deudores y crédito comercial
            </p>
          </div>
        </div>

        <button 
          data-onboarding-id="client-new-btn"
          onClick={() => onNavigate('clientes-nuevo')}
          className="w-full sm:w-auto justify-center bg-[#3ff2aa] text-[#0B2A36] px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all"
        >
          <UserPlus size={16} strokeWidth={3} /> Nuevo Registro
        </button>
      </div>

      {/* HEADER ANTERIOR (COMENTADO)
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 px-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm">
            <Users size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 uppercase tracking-tight leading-none">Cartera de Clientes</h1>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">Administración de deudores y crédito comercial</p>
          </div>
        </div>
        <button 
          onClick={() => onNavigate('clientes-nuevo')}
          className="bg-[#3ff2aa] text-[#0B2A36] px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-md hover:scale-[1.02] active:scale-95 transition-all shadow-emerald-50"
        >
          <UserPlus size={16} strokeWidth={3} /> Nuevo Registro
        </button>
      </div>
      */}

      {/* Mensajería inline (sin popouts) */}
      {uiMessage?.text && (
        <div
          className={`mb-4 rounded-2xl border px-4 py-3 text-[11px] font-bold ${
            uiMessage.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-700'
              : uiMessage.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-slate-50 border-slate-200 text-slate-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Info size={14} className="opacity-70" />
              <span className="leading-snug">{uiMessage.text}</span>
            </div>
            <button
              onClick={() => setUiMessage(null)}
              className="px-3 py-1 rounded-xl bg-white border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* KPIS NUEVO ESTILO PRODUCTOS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-[#51A7FD]/30 transition-all duration-300 group">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cuentas Activas</p>
                <div className="p-1.5 rounded-lg bg-blue-50 text-[#51A7FD] group-hover:scale-110 transition-transform">
                  <Users size={16} />
                </div>
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight">{clientes.length}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Clientes registrados</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-rose-300/30 transition-all duration-300 group">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Deuda Consolidada</p>
                <div className="p-1.5 rounded-lg bg-rose-50 text-rose-500 group-hover:scale-110 transition-transform">
                  <DollarSign size={16} />
                </div>
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-rose-600 tracking-tight">$ {stats.deuda.toLocaleString('es-AR')}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Saldo pendiente de cobro</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300/30 transition-all duration-300 group sm:col-span-2 xl:col-span-1">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cupo de Crédito</p>
                <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform">
                  <CreditCard size={16} />
                </div>
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-emerald-600 tracking-tight">$ {stats.credito.toLocaleString('es-AR')}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Límite global asignado</p>
        </div>
      </div>

      {/* KPIS ANTERIORES (COMENTADO)
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 transition-all hover:border-[#51A7FD]">
          <div className="p-3 rounded-xl bg-blue-50 text-[#51A7FD] shadow-inner"><Users size={24} /></div>
          <div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Cuentas Activas</p>
            <h3 className="text-xl font-black text-slate-800 tracking-tighter">{clientes.length}</h3>
          </div>
        </div>
        ...
      </div>
      */}

      {/* BUSCADOR NUEVO ESTILO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
              type="text" placeholder="BUSCAR POR NOMBRE, CUIT O DNI..."
              className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {allowClientDeactivation && (
            <div className="inline-flex rounded-2xl border border-slate-100 bg-slate-50 p-1 shadow-inner">
              {CLIENT_STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${statusFilter === opt.value
                    ? 'bg-[#0B2A36] text-[#3FF2AA] shadow'
                    : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* BUSCADOR ANTERIOR (COMENTADO)
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
        <input 
          type="text" placeholder="BUSCAR POR NOMBRE, CUIT O DNI..." 
          className="w-full bg-white border border-slate-200 pl-12 pr-4 py-4 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-4 focus:ring-[#51A7FD]/5 focus:border-[#51A7FD] transition-all shadow-sm shadow-slate-50"
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      */}

      {/* LISTADO SIN MENÚ (ESTILO PRODUCTOS) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10 min-h-[420px]">
        
        {/* HEADER TABLA */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
            <Users size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Listado de Clientes ({filteredClientes.length})
            </span>
            </div>
        </div>

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
            {sortedClientes.map((cliente, idx) => {
              const isInactive = allowClientDeactivation && cliente.is_active === false;
              return (
                <button
                  key={`mobile-${cliente.id}`}
                  onClick={() => onNavigate('clientes-detalle', cliente.id)}
                  className={`w-full text-left bg-white border rounded-2xl p-3 shadow-sm hover:border-[#51A7FD]/30 transition-all ${isInactive ? 'border-rose-100 opacity-80' : 'border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center border shadow-sm shrink-0 ${isInactive ? 'bg-rose-50 text-rose-500 border-rose-100' : 'bg-slate-50 text-slate-400 border-slate-100'}`}>
                        <User size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">#{idx + 1}</p>
                          {isInactive && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-50 border border-rose-100 text-rose-700 text-[8px] font-black uppercase tracking-widest">
                              <Power size={8} /> Inactivo
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[12px] font-black text-slate-700 uppercase truncate">{cliente.full_name}</p>
                        <p className="mt-1 text-[9px] font-bold text-slate-400 uppercase">{cliente.doc_type || 'CUIT'}: {cliente.doc_number || '-'}</p>
                        {cliente.phone && <p className="mt-1 text-[9px] font-bold text-slate-300">{cliente.phone}</p>}
                      </div>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                      <div>
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Saldo</p>
                        <p className={`mt-1 text-[12px] font-black ${getClientBalanceValue(cliente) > 0 ? 'text-rose-600' : 'text-[#51A7FD]'}`}>
                          $ {getClientBalanceValue(cliente).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      {isAdmin && allowClientDeactivation && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => requestStatusChange(cliente, e)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') requestStatusChange(cliente, e); }}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border shadow-sm cursor-pointer ${isInactive
                            ? 'bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-rose-50 border-rose-100 text-rose-700 hover:bg-rose-100'}`}
                        >
                          <Power size={10} /> {isInactive ? 'Activar' : 'Desactivar'}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            {sortedClientes.length === 0 && (
              <div className="py-16 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                No se encontraron clientes
              </div>
            )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left min-w-[820px]">
            <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <SortableTh label="Ref" sortKey="ref" />
                <SortableTh label="Identificacion Legajo" sortKey="identificacion" />
                <SortableTh label="Saldo Deudor" sortKey="balance" align="right" />
                {allowClientDeactivation && (
                  <th className="px-3.5 py-3.5 w-32 text-right">Estado</th>
                )}
                <th className="px-3.5 py-3.5 w-10"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
                {sortedClientes.map((cliente, idx) => {
                  const isInactive = allowClientDeactivation && cliente.is_active === false;
                  return (
                <tr
                    key={cliente.id}
                    onClick={() => onNavigate('clientes-detalle', cliente.id)}
                    className={`hover:bg-blue-50/20 transition-all cursor-pointer group ${isInactive ? 'bg-rose-50/20' : ''}`}
                >
                    <td className="px-3.5 py-3.5">
                    <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">#{idx + 1}</span>
                    </td>
                    <td className="px-3.5 py-3.5">
                    <div className="flex items-center gap-4">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border shadow-sm transition-all shrink-0 ${isInactive
                          ? 'bg-rose-50 text-rose-500 border-rose-100'
                          : 'bg-slate-50 text-slate-400 border-slate-100 group-hover:bg-[#51A7FD] group-hover:text-white'}`}>
                        <User size={16} />
                        </div>
                        <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-black uppercase tracking-tight transition-colors leading-none truncate ${isInactive ? 'text-slate-500' : 'text-slate-700 group-hover:text-[#51A7FD]'}`}>
                              {cliente.full_name}
                          </span>
                          {isInactive && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-50 border border-rose-100 text-rose-700 text-[8px] font-black uppercase tracking-widest">
                              <Power size={8} /> Inactivo
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                            {cliente.doc_type || 'CUIT'}: {cliente.doc_number || '-'}
                            </span>
                            {cliente.phone && <span className="text-[9px] text-slate-300 font-bold">| {cliente.phone}</span>}
                        </div>
                        </div>
                    </div>
                    </td>
                    <td className="px-3.5 py-3.5 text-right">
                    <span className={`text-[12px] font-black tracking-tighter ${getClientBalanceValue(cliente) > 0 ? 'text-rose-600' : 'text-[#51A7FD]'}`}>
                        $ {getClientBalanceValue(cliente).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </span>
                    </td>
                    {allowClientDeactivation && (
                      <td className="px-3.5 py-3.5 text-right">
                        {isAdmin ? (
                          <button
                            type="button"
                            onClick={(e) => requestStatusChange(cliente, e)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border shadow-sm transition-all ${isInactive
                              ? 'bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-rose-50 border-rose-100 text-rose-700 hover:bg-rose-100'}`}
                          >
                            <Power size={10} /> {isInactive ? 'Activar' : 'Desactivar'}
                          </button>
                        ) : (
                          <span className={`text-[9px] font-black uppercase tracking-widest ${isInactive ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {isInactive ? 'Inactivo' : 'Activo'}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-3.5 py-3.5 text-right">
                    <ArrowRight size={18} className="text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all" />
                    </td>
                </tr>
                  );
                })}
                {sortedClientes.length === 0 && (
                     <tr>
                        <td colSpan={allowClientDeactivation ? 5 : 4} className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                            No se encontraron clientes
                        </td>
                     </tr>
                )}
            </tbody>
            </table>
        </div>      </div>

      {statusModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-4 flex items-center gap-3">
              <ShieldAlert size={18} className="text-[#3FF2AA]" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                {statusModal.mode === 'deactivate' ? 'Desactivar cliente' : 'Reactivar cliente'}
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cliente</p>
                <p className="mt-1 text-[12px] font-black text-slate-800 uppercase">{statusModal.client?.full_name}</p>
                {Number(getClientBalanceValue(statusModal.client) || 0) > 0 && (
                  <p className="mt-1 text-[10px] font-bold text-rose-600">
                    Saldo deudor: $ {getClientBalanceValue(statusModal.client).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>

              {statusModal.mode === 'deactivate' ? (
                <>
                  <ul className="text-[11px] font-bold text-slate-600 leading-relaxed list-disc pl-5 space-y-1">
                    <li>Oculto de selectores de proyectos, tickets y reuniones del CRM.</li>
                    <li>Bloqueado en la tienda online y portal de cliente.</li>
                    <li>Filtrado de reportes en modo "Activos"; historial intacto.</li>
                  </ul>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                      Motivo (opcional)
                    </label>
                    <textarea
                      rows={3}
                      value={statusModal.reason}
                      onChange={(e) => setStatusModal((s) => ({ ...s, reason: e.target.value }))}
                      placeholder="Ej: cuenta cerrada, migrado, etc."
                      className="w-full bg-slate-50 border border-slate-100 px-4 py-2.5 rounded-xl text-[11px] font-bold tracking-tight outline-none focus:border-[#51A7FD] resize-none"
                    />
                  </div>
                </>
              ) : (
                <p className="text-[12px] font-bold text-slate-700 leading-relaxed">
                  Volverá a aparecer en selectores, reportes y tienda. Se limpiará la fecha y motivo de baja anteriores.
                </p>
              )}

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStatusModal(null)}
                  disabled={savingStatus}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmStatusChange}
                  disabled={savingStatus}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md disabled:opacity-50 ${statusModal.mode === 'deactivate'
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-[#3FF2AA] text-[#0B2A36] hover:opacity-90'}`}
                >
                  {savingStatus ? 'Guardando...' : (statusModal.mode === 'deactivate' ? 'Confirmar baja' : 'Reactivar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;

